import { endOfDay } from 'date-fns'
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import { SidebarHeader } from 'react-calendar-timeline'

import { Box } from '@mui/material'

import { useAppDispatch, useAppSelector } from '../../../../../hooks/storeHooks'
import { useInternalAccessCheck } from '../../../../account/hooks/roleHooks'
import { useCalendarEventsByGame } from '../../../hooks/liveEventsCalendarEvents'
import { gameCalendarVisibilityToggled, selectVisibleGameCalendars } from '../../../slices/liveEventsCalendarsSlice'
import { CalendarTimeRangeChangeCallback, CalendarTimeRanges } from '../../../types/Calendar'
import { GameTopIAPsByDate } from '../../../types/GameTopIAPsByDate'
import { GameVersion } from '../../../types/GameVersion'
import { LiveEventsCalendarTimelineType, TrackingEventTimelineGroup, TrackingEventTimelineItem } from '../../../types/LiveEvents'
import { PerformanceEffect } from '../../../types/PerformanceEffect'
import { TrackedGame } from '../../../types/TrackedGame'
import { EstimateValue, LiveEventReview, PerformanceChange, TrackingEvent, TrackingEventTimeRange } from '../../../types/TrackingEvents'
import LiveEventsCalendar from '../LiveEventsCalendar'
import { LiveEventsCalendarCustomSidebarHeader } from '../LiveEventsCalendarCustomSidebarHeader/LiveEventsCalendarCustomSidebarHeader'

/**
 * Container component that prepares event data to be shown for a specific game only
 */
type LiveEventsCalendarByGameContainerProps = {
  trackedGames: TrackedGame[]
  trackedGame: TrackedGame
  trackedEvents: TrackingEvent[]
  gameStats?: { trackingStarted: number; trackingEnded: number }
  allEventsMaxEnd?: number
  loadedEventsTimeRange?: TrackingEventTimeRange
  performanceEffects: PerformanceEffect[]
  gameVersions: GameVersion[]
  gameTopIAPs: GameTopIAPsByDate[]
  analystReviews: LiveEventReview[]
  performanceChanges?: PerformanceChange[] | undefined
  calendarTimeRanges: CalendarTimeRanges
  timeStart?: number
  timeEnd?: number
  minZoom?: number
  maxZoom?: number
  highlightedEventId?: string
  selectedCalendarAdditionalDataIdsMap: { [dataId: string]: boolean }
  onItemClick: (clickedItem: TrackingEventTimelineItem) => void
  onGroupClick: (clickedGroup: TrackingEventTimelineGroup) => void
  onArchetypeClick: (clickedGroup: TrackingEventTimelineGroup) => void
  onDeselectGame?: (trackedGame: TrackedGame, hdGameSelected: boolean) => void
  onOverviewTrackedGameChanged: (trackedGame: TrackedGame) => void
  onCalendarTimeRangeChange: CalendarTimeRangeChangeCallback
  onGamePerformanceDialogOpen: (trackedGame: TrackedGame) => void
  onCalendarChange?: () => void
  isLoading?: boolean
  children?: ReactNode
  performanceValues?: EstimateValue[] | undefined
  groupByArchetype?: boolean
  hideDiscontinuedArchetypes?: boolean
}

export const LiveEventsCalendarByGameContainer: FC<LiveEventsCalendarByGameContainerProps> = ({
  calendarTimeRanges,
  gameVersions,
  performanceEffects,
  gameTopIAPs,
  analystReviews,
  performanceChanges,
  gameStats,
  allEventsMaxEnd,
  loadedEventsTimeRange,
  trackedEvents,
  trackedGames,
  trackedGame,
  timeStart,
  timeEnd,
  minZoom,
  maxZoom,
  highlightedEventId,
  selectedCalendarAdditionalDataIdsMap,
  groupByArchetype,
  onItemClick,
  onGroupClick,
  onArchetypeClick,
  onDeselectGame,
  onOverviewTrackedGameChanged,
  onCalendarTimeRangeChange,
  onGamePerformanceDialogOpen,
  onCalendarChange,
  isLoading,
  performanceValues,
  hideDiscontinuedArchetypes,
}) => {
  const isInternalUser = useInternalAccessCheck()
  const { items, groups } = useCalendarEventsByGame({
    selectedCalendarAdditionalDataIdsMap,
    trackedGame,
    trackedEvents,
    performanceEffects,
    gameVersions,
    gameTopIAPs,
    analystReviews,
    highlightedEventId,
    calendarTimeStart: calendarTimeRanges[trackedGame.game.id]?.timeStart || timeStart,
    calendarTimeEnd: calendarTimeRanges[trackedGame.game.id]?.timeEnd || timeEnd,
    performanceValues,
    performanceChanges,
    groupByArchetype,
    hideDiscontinuedArchetypes,
  })

  const maxVisibleTrackingEventEndTime = useMemo(() => {
    return items.reduce((max, item) => {
      if (item.type !== LiveEventsCalendarTimelineType.TrackingEvent) {
        return max
      }

      return item.end_time > max ? item.end_time : max
    }, 0)
  }, [items])

  const trackingEventTimeRange = useMemo(() => {
    if (!gameStats) return { minTimeData: 0, maxTimeData: 0 }
    const minDate = new Date(gameStats.trackingStarted)
    const maxDate = new Date(gameStats.trackingEnded)
    minDate.setHours(0, 0, 0, 0)
    maxDate.setHours(23, 59, 59, 999)
    let maxTimeData = maxDate.getTime()

    if (!isInternalUser && allEventsMaxEnd && allEventsMaxEnd > 0) {
      maxTimeData = allEventsMaxEnd
    } else if (!isInternalUser && maxVisibleTrackingEventEndTime > 0) {
      maxTimeData = maxVisibleTrackingEventEndTime
    } else if (!isInternalUser && trackedEvents.length > 0) {
      const maxEventEnd = trackedEvents.reduce((max, e) => {
        const eventEnd = endOfDay(new Date(e.end)).getTime()
        return eventEnd > max ? eventEnd : max
      }, 0)

      if (maxEventEnd > 0) {
        maxTimeData = maxEventEnd
      }
    }

    return { minTimeData: minDate.getTime(), maxTimeData }
  }, [gameStats, isInternalUser, allEventsMaxEnd, maxVisibleTrackingEventEndTime, trackedEvents])

  const loadedEventsTimeRangeForMarkers = isInternalUser ? loadedEventsTimeRange : trackingEventTimeRange
  const dispatch = useAppDispatch()
  const containerRef = useRef<HTMLDivElement>(null)
  const visibleGameCalendars = useAppSelector(selectVisibleGameCalendars)
  const calendarHidden = !visibleGameCalendars?.includes(trackedGame.game.id)

  const sidebarHeader = useMemo(() => {
    return (
      <SidebarHeader>
        {({ getRootProps }) => (
          <LiveEventsCalendarCustomSidebarHeader
            trackedGames={trackedGames}
            trackedGame={trackedGame}
            onDeselectGame={onDeselectGame}
            onToggleVisibility={() => dispatch(gameCalendarVisibilityToggled(trackedGame.game.id))}
            onOverviewTrackedGameChanged={onOverviewTrackedGameChanged}
            onGamePerformanceDialogOpen={onGamePerformanceDialogOpen}
            {...getRootProps({ style: { color: 'inherit' } })}
            calendarContainerRef={containerRef}
          />
        )}
      </SidebarHeader>
    )
  }, [trackedGames, trackedGame, onDeselectGame, onOverviewTrackedGameChanged, onGamePerformanceDialogOpen, dispatch])

  const handleCalendarTimeRangeChange = useCallback(
    (timeStart: number, timeEnd: number, updateScrollCanvas: (start: number, end: number) => void) => {
      updateScrollCanvas(timeStart, timeEnd)
      onCalendarTimeRangeChange(trackedGame.game.id, timeStart, timeEnd)
    },
    [onCalendarTimeRangeChange, trackedGame.game.id]
  )

  useEffect(() => {
    onCalendarChange?.()
  }, [onCalendarChange, groups, items, calendarHidden])

  return (
    <Box ref={containerRef}>
      <LiveEventsCalendar
        trackingEventTimeRange={trackingEventTimeRange}
        groups={groups}
        items={items}
        onItemClick={onItemClick}
        onGroupClick={onGroupClick}
        onArchetypeClick={onArchetypeClick}
        sidebarHeader={sidebarHeader}
        timeStart={calendarTimeRanges[trackedGame.game.id]?.timeStart || timeStart}
        timeEnd={calendarTimeRanges[trackedGame.game.id]?.timeEnd || timeEnd}
        minZoom={minZoom}
        maxZoom={maxZoom}
        isLoading={items.length === 0 && isLoading}
        calendarHidden={calendarHidden}
        lineHeight={34}
        onCalendarTimeRangeChange={handleCalendarTimeRangeChange}
        groupByArchetype={groupByArchetype}
        loadedEventsTimeRange={loadedEventsTimeRangeForMarkers}
      />
    </Box>
  )
}
